
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { CateringPackage, CateringPackageItem } from '@/types/cateringBot';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';

const namespace: string = 'cateringBot';

@Component<CateringBotPackageCard>({ components: { LoadingSpinner } })
export default class CateringBotPackageCard extends Vue {
	@Prop({ required: true }) readonly pkg!: CateringPackage;
	@Prop({ required: true }) readonly index!: number;
	@Prop({ default: true }) readonly showRefineLink!: boolean;

	@Getter('canRefine', { namespace }) private canRefine!: boolean;
	@Getter('refiningIndex', { namespace }) private refiningIndex!: number | null;
	@Getter('creditsRemaining', { namespace: 'rallyIntelligence' }) private creditsRemaining!: number;

	@Action('refinePackage', { namespace }) private refinePackage!: (p: { index: number; refinementText: string }) => Promise<void>;
	@Action('addPackageItemToCart', { namespace }) private addPackageItemToCart!: (item: CateringPackageItem) => Promise<void>;

	@Watch('pkg', { immediate: true })
	private onPkgChange(): void {
		if (!(this.pkg.is_refined && this.index === 0)) {
			this.animateRefined = false;
			return;
		}
		// Toggle off → on across two animation frames so the browser sees a class
		// transition and restarts the CSS animation on each new refinement.
		this.animateRefined = false;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.animateRefined = true;
			});
		});
	}

	private isItemDetailsOpen: boolean = true;
	private isRefineOpen: boolean = false;
	private isAdding: boolean = false;
	private animateRefined: boolean = false;
	private refinementText: string = '';
	private errorMessage: string = '';

	private async onApplyRefinement(): Promise<void> {
		try {
			this.errorMessage = '';
			await this.refinePackage({ index: this.index, refinementText: this.refinementText.trim() });
			this.isRefineOpen = false;
			this.refinementText = '';
			this.$emit('refined');
		}
		catch (err) {
			const code = (err as any)?.response?.data?.code;
			if (code === 'rally_content_filter') {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.content_filter');
			}
			else if (code === 'rally_token_limit_exceeded') {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.token_limit');
			}
			else {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.unavailable');
			}
		}
	}

	private async onAddToCart(): Promise<void> {
		this.isAdding = true;
		let added = 0;

		try {
			for (const item of this.pkg.items) {
				await this.addPackageItemToCart(item);
				added += 1;
			}
			this.$emit('close');
		}
		catch {
			const message = added > 0
				? this.$t('rallyIntelligence.cateringBot.errors.add_to_cart_partial', { added, total: this.pkg.items.length })
				: this.$t('rallyIntelligence.cateringBot.errors.add_to_cart_failed');
			this.$toasted.show(message, { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.isAdding = false;
		}
	}
}
